/**
 * Settings, as two panes.
 *
 * Left: the six categories. Right: the one selected. Below 60rem the two
 * panes become one column - the category list first, then the panel - which is
 * a *layout* change and not a different screen: the same components, the same
 * routes, no mobile-only code path to keep in step.
 *
 * The selected category is in the URL rather than in state. Three things come
 * from that and none of them are free otherwise: a link to
 * `/ayarlar/gizlilik` opens on Privacy, Back leaves a category instead of
 * leaving Settings, and a reload stays where the reader was.
 *
 * The panels are ordinary sections, so nothing here knows what a setting is.
 * What this file owns is navigation and the one rule the navigation must obey:
 * a category is only listed if its panel has something real in it. There is no
 * "coming soon" entry, and no category that opens onto an empty pane.
 */

import { NavLink, Outlet, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/product";
import { paths } from "@/app/paths";
import { SETTINGS_CATEGORIES } from "./categories";
import "./settings.css";

export function SettingsShell() {
  const location = useLocation();

  return (
    <div className="stack">
      <PageHeader
        title="Ayarlar"
        subtitle="Hesabınız, tercihleriniz ve bu kurulumun yapabildikleri."
      />

      <div className="settings2">
        <nav className="settings2__nav" aria-label="Ayar kategorileri">
          <ul>
            {SETTINGS_CATEGORIES.map((category) => (
              <li key={category.slug}>
                <NavLink
                  to={`${paths.settings()}/${category.slug}`}
                  className="settings2__link"
                  // The pane on the right is the accessible answer to "which
                  // one is selected"; `aria-current` is what says so to a
                  // screen reader, which cannot see the highlight.
                  end
                >
                  <span className="settings2__link-label">{category.label}</span>
                  <span className="settings2__link-summary">{category.summary}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/*
          Keyed on the path so switching category resets the panel's own state.
          Without it, a half-typed password would survive a trip to Appearance
          and back, which is both surprising and a credential sitting in memory
          for longer than the form is on screen.
        */}
        <section className="settings2__panel" key={location.pathname}>
          <Outlet />
        </section>
      </div>
    </div>
  );
}
